import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const YouTubeCarousel = () => {
  const [videos, setVideos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos/carousel`);
      setVideos(response.data || []);
      setError(false);
    } catch (e) {
      console.error('Failed to load carousel videos:', e);
      setError(true);
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const nextVideo = () => {
    setCurrentIndex((prev) => (prev + 1) % videos.length);
  };

  const prevVideo = () => {
    setCurrentIndex((prev) => (prev - 1 + videos.length) % videos.length);
  };

  if (loading) {
    return null; // Don't show loading spinner, just hide
  }

  if (error || videos.length === 0) {
    return null; // Don't show anything if error or no videos
  }

  const currentVideo = videos[currentIndex];

  return (
    <div className="w-full max-w-4xl mx-auto mb-8">
      {/* Video Container */}
      <div className="relative bg-black rounded-2xl overflow-hidden shadow-2xl">
        {/* YouTube Embed */}
        <div className="relative pt-[56.25%]"> {/* 16:9 aspect ratio */}
          <iframe
            className="absolute top-0 left-0 w-full h-full"
            src={`https://www.youtube.com/embed/${currentVideo.youtube_id}?rel=0`}
            title={currentVideo.title || 'Video'}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        {/* Navigation Arrows - Only show if more than 1 video */}
        {videos.length > 1 && (
          <>
            <button
              onClick={prevVideo}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all z-10"
            >
              <ChevronLeft className="w-6 h-6 text-gray-800" />
            </button>
            <button
              onClick={nextVideo}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all z-10"
            >
              <ChevronRight className="w-6 h-6 text-gray-800" />
            </button>
          </>
        )}
      </div>

      {/* Video Title */}
      {currentVideo.title && (
        <p className="text-center mt-3 text-gray-600 font-medium">
          {currentVideo.title}
        </p>
      )}

      {/* Dots Indicator - Only show if more than 1 video */}
      {videos.length > 1 && (
        <div className="flex justify-center mt-4 space-x-2">
          {videos.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                idx === currentIndex
                  ? 'bg-pink-500 w-6'
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default YouTubeCarousel;
